import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
	Button,
	Calendar,
	DrawerBodyWrapper,
	DrawerFooterWrapper,
	Input,
	PageLevelTableWrapper,
	RadioButton,
	SectionHeader,
	Select,
	Table,
	TOAST_TYPE,
} from '@base'
import { DISPATCH_PLAN_VEHICLE_STATUS, enquiryInputType, DispatchPaymentStatus, LOGISTICS_HANDLER_TYPES, strings } from '@constants'
import { createDispatchPlan } from '@data'
import { getApprovedFulfillmentPlansApi } from '@data/conn/get/shipmentRequest'
import convertPagableData from '@data/conn/convertPagableData'
import { showLoader, showToast } from '@data/state/action'
import { capitalizeWord, displayAmount, displayName } from '@utils'
import { BiInputWrapper } from '@pages/Admin/Lead/AddLead/AddLead'
import { User } from '@data/model'
import { toggleDrawer } from '@data/state/action/root'
import { ListItemWrapper, ListWrapper } from '@pages/Admin/Enquiry/ManageEnquiries/ManageEnquiry/PaymentTerms'
import { ButtonsWrapper, Label, RadioButtonsWrapper, RadioButtonWrapper } from '../style'

const inputTypes = Object.entries(enquiryInputType)?.map(([key, value]) => ({ key, label: value }))
const dsispatchPaymentStatus = Object.entries(DispatchPaymentStatus)?.map(([key, value]) => ({ key, label: value }))
const plannedTypes = [{ key: 'TRUE' }, { key: 'FALSE' }]

const deriveFormState = (data) => {
	const state = {
		limit_available: {
			id: 'limit-available',
			label: strings('limit_available'),
			placeholder: strings('enter', 'limit_available'),
			value: '',
			disabled: false,
			required: false,
			dirty: false,
		},
		plan_start_date: {
			id: 'plan-start-date',
			label: strings('plan_start_date'),
			placeholder: strings('select', 'plan_start_date'),
			value: '',
			disabled: false,
			required: false,
			dirty: false,
		},
		// current date
		plan_current_date: {
			id: 'plan-current-date',
			label: strings('plan_current_date'),
			placeholder: strings('select', 'plan_current_date'),
			value: '',
			dirty: false,
			disabled: false,
			required: false,
		},
		quantity: {
			id: 'quantity',
			label: strings('quantity'),
			placeholder: strings('enter', 'quantity'),
			value: '',
			disabled: false,
			required: false,
			dirty: false,
		},

		remarks: {
			id: 'serviced',
			label: strings('remarks'),
			value: [],
			dirty: false,
			disabled: false,
			required: false,
		},
		approved: {
			id: 'approved',
			label: strings('approved'),
			value: '',
			disabled: false,
			required: false,
			dirty: false,
		},
		scheduled_list: {
			id: 'scheduled-list',
			label: strings('scheduled_list'),
			placeholder: strings('enter', 'scheduled_list'),
			value: '',
			disabled: false,
			required: false,
			dirty: false,
		},
		new_remark: {
			id: 'new-remark',
			label: strings('remark'),
			placeholder: strings('enter', 'remark'),
			value: '',
			disabled: false,
			required: false,
			dirty: false,
		},
		original_supplier_po_number: {
			id: 'original-supplier-po-number',
			value: '',
			required: false,
		},
		input_type: {
			id: 'input-type',
			label: strings('input', 'type'),
			value: enquiryInputType.WEIGHT,
			primary_key: 'key',
			display_key: 'label',
			disabled: false,
			required: false,
		},
		logistics_handler: {
			id: 'logistics-handler',
			label: strings('logistics_handler'),
			value: '',
			primary_key: 'key',
			display_key: 'label',
			disabled: false,
			required: false,
		},
		planned: {
			value: 'TRUE',
		},
		payment_status: {
			id: 'payment-status',
			label: strings('payment', 'status'),
			value: null,
			primary_key: 'key',
			display_key: 'label',
			disabled: false,
			required: false,
		},
	}

	if (data) {
	}
	return state
}

const deriveVehicleFormState = () => {
	const state = {
		driver_number: {
			id: 'driver-number',
			value: '',
			label: strings('driver', 'number'),
			placeholder: strings(['enter', 'driver', 'number']),
			disabled: false,
			required: false,
		},
		vehicle_number: {
			id: 'vehicle-number',
			placeholder: strings(['enter', 'vehicle', 'number']),
			label: strings('vehicle', 'number'),
			value: '',
			valid: true,
			disabled: false,
			required: false,
		},
		vehicle_status: {
			id: 'vehicle-status',
			label: strings('vehicle', 'status'),
			placeholder: strings(['enter', 'vehicle', 'status']),
			value: '',
			valid: true,
			disabled: false,
			required: false,
		},
		dispatch_date: {
			id: 'dispatch-date',
			label: strings('dispatch', 'date'),
			value: new Date(),
			disabled: false,
			required: false,
		},
	}
	return state
}

// each option is an approved fulfillment plan of a shipment request
const displayOrderName = (data) => `${data?.order_id} | ${data?.supplier_po_number} | ${data?.supplier?.organisation_name ?? '-'}`

const vehicleStatusTypes = Object.entries(DISPATCH_PLAN_VEHICLE_STATUS).map(([key, value]) => ({ key, label: value }))
const logisticsHandlerTypes = Object.entries(LOGISTICS_HANDLER_TYPES).map(([key, value]) => ({ key, label: value }))

const AddDispatchPlan = ({ update, dispatchPlan = null, edit = false }) => {
	const dispatch = useDispatch()
	const loggedInUser = useSelector((_s) => _s.getIn(['user', 'user']))
	const [formState, setFormState] = useState(deriveFormState())
	const [orderList, setOrderList] = useState([])
	const [selectedOrder, setSelectedOrder] = useState()
	const [selectedSupplier, setSelectedSupplier] = useState()
	const [supplierList, setSupplierList] = useState()
	const [vehicleDetails, setVehicleDetails] = useState([])
	const [vehicleFormState, setVehicleFormState] = useState(deriveVehicleFormState())

	useEffect(() => {
		if (edit) {
		} else {
			dispatch(showLoader(true))
			getApprovedFulfillmentPlansApi({ page: 0, size: 1000 })
				.then((response) => {
					let _response = convertPagableData(response)?.data?.map((item) => ({ ...item, display_name: displayOrderName(item) }))
					setOrderList(_response)
				})
				.catch((err) => {
					let error_msg = err?.message ?? 'Some error occurred'
					dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
				})
				.finally(() => {
					dispatch(showLoader(false))
				})
		}
	}, [edit])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value,
			},
		}))
	}

	const updateVehicleFormState =
		(key, type = 'value') =>
		(value) => {
			if (type === 'valid') {
				setVehicleFormState((_state) => ({ ..._state, [key]: { ..._state[key], valid: value } }))
			} else {
				setVehicleFormState((_state) => ({ ..._state, [key]: { ..._state[key], value: value } }))
			}
		}

	const formStateToPlan = () => {
		return {
			enquiry_id: selectedOrder?.enquiry_id,
			order_id: selectedOrder?.order_id,
			buyer: selectedOrder?.buyer,
			supplier: new User(selectedSupplier).getUserDetails(),
			region: selectedOrder?.region,
			supplier_po_number: selectedOrder?.supplier_po_number,
			dispatch_quantity: formState?.quantity?.value * 1000,
			plan_start_date: formState?.plan_start_date?.value,
			original_supplier_po_number: selectedSupplier?.original_supplier_po_number,
			supply_person: new User(selectedOrder?.supply_manager).getUserDetails(),
			payment_terms_list: selectedOrder?.payment_terms_list,
			original_buyer_po_number: selectedOrder?.original_buyer_po_number,
			input_type: formState?.input_type?.value,
			logistics_handler: formState?.logistics_handler?.value,
			expected_amount: formState?.quantity?.value * 1000 * selectedSupplier?.price_per_unit,
			// remarks_list: formState?.new_remark?.value === '' ? null : [formState?.new_remark?.value],
			expected_delivery_date: selectedSupplier?.expected_transit_time,
			buyer_po_date: selectedOrder?.buyer_po_date,
			vehicle_driver_details: vehicleDetails,
			planned: formState?.planned?.value === 'TRUE' ? true : false,
			payment_status: formState?.payment_status?.value,
		}
	}

	const onAddHandler = () => {
		const _dispatchPlan = formStateToPlan()
		dispatch(showLoader(true))
		createDispatchPlan(_dispatchPlan, loggedInUser?.user_id)
			.then((response) => {
				dispatch(showToast(true, strings('msg_dispatch_plan_created_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				let error_msg = error?.response?.data?.message ?? strings('msg_dispatch_plan_created_failure')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
				dispatch(toggleDrawer(false))
				update()
			})
	}

	const selectOrderHandler = (value) => {
		setSelectedOrder(value)
		// a fulfillment plan has a single supplier
		let _supplierList = value?.supplier
			? [
					{
						...value.supplier,
						display_name: displayName(value.supplier),
						original_supplier_po_number: value?.supplier_po_number,
						price_per_unit: 1,
					},
				]
			: []
		setSupplierList(_supplierList)
		setSelectedSupplier(_supplierList?.[0] ?? {})
		setFormState((_s) => ({
			..._s,
			region: {
				..._s['region'],
				value: value?.region,
			},
			logistics_handler: {
				..._s['logistics_handler'],
				value: value?.logistics_handler,
			},
		}))
	}

	const checkDisabled = useMemo(() => {
		if (
			formState?.plan_start_date?.value === '' ||
			formState?.input_type?.value === '' ||
			formState?.quantity?.value === '' ||
			formState?.quantity?.value <= 0 ||
			formState?.payment_status?.value === ''
		) {
			return true
		}
		return false
	}, [formState])

	const submitVehicleDetails = () => {
		setVehicleDetails((_state) => [
			..._state,
			{
				vehicle_number: vehicleFormState?.vehicle_number?.value,
				driver_number: vehicleFormState?.driver_number?.value,
				vehicle_status: vehicleFormState?.vehicle_status?.value,
				dispatch_date: new Date(vehicleFormState?.dispatch_date?.value).getTime(),
			},
		])
	}

	const VehicleColumsn = [
		{ label: strings('vehicle', 'number'), key: 'vehicle_number', mobile: true, align: 'left' },
		{ label: strings('driver', 'number'), key: 'driver_number', mobile: true, align: 'left' },
		{ label: strings('status'), key: 'vehicle_status', mobile: true },
	]

	const vehicleDetailsDisabled = useMemo(() => {
		return Boolean(!vehicleFormState?.vehicle_number?.value || !vehicleFormState?.driver_number?.value || !vehicleFormState?.driver_number?.valid)
	}, [vehicleFormState])

	const renderVehicleDetails = () => {
		return (
			<>
				<SectionHeader></SectionHeader>

				<BiInputWrapper>
					<Input
						id={vehicleFormState?.vehicle_number?.id}
						label={vehicleFormState?.vehicle_number?.label}
						placeholder={vehicleFormState?.vehicle_number?.placeholder}
						value={vehicleFormState?.vehicle_number?.value}
						disabled={vehicleFormState?.vehicle_number?.disabled}
						required={vehicleFormState?.vehicle_number?.required}
						onChange={updateVehicleFormState('vehicle_number')}
					/>
					<Input
						id={vehicleFormState?.driver_number?.id}
						label={vehicleFormState?.driver_number?.label}
						placeholder={vehicleFormState?.driver_number?.placeholder}
						value={vehicleFormState?.driver_number?.value}
						pattern='[789][0-9]{9}'
						errorMap={{
							patternMismatch: `${strings('enter_valid_num')}`,
							valueMissing: `${strings('mob_num_required')}`,
						}}
						setValidity={updateVehicleFormState('driver_number', 'valid')}
						maxLength={10}
						disabled={vehicleFormState?.driver_number?.disabled}
						required={vehicleFormState?.driver_number?.required}
						onChange={updateVehicleFormState('driver_number')}
					/>
				</BiInputWrapper>
				<BiInputWrapper>
					<Select
						id={vehicleFormState?.vehicle_status?.id}
						label={vehicleFormState?.vehicle_status?.label}
						placeholder={vehicleFormState?.vehicle_status?.placeholder}
						data={vehicleStatusTypes}
						primaryKey='key'
						displayKey='label'
						value={vehicleStatusTypes?.find((item) => item.key === vehicleFormState?.vehicle_status?.value) ?? {}}
						disabled={vehicleFormState?.vehicle_status?.disabled}
						required={vehicleFormState?.vehicle_status?.required}
						onChange={(val) => updateVehicleFormState('vehicle_status')(val.key)}
					/>
					<Calendar
						id={vehicleFormState?.dispatch_date?.id}
						label={vehicleFormState?.dispatch_date?.label}
						placeholder={vehicleFormState?.dispatch_date?.placeholder}
						value={vehicleFormState?.dispatch_date?.value}
						onChange={updateVehicleFormState('dispatch_date')}
						disabled={vehicleFormState?.dispatch_date?.disabled}
						required={vehicleFormState?.dispatch_date?.required}
					/>
				</BiInputWrapper>
				<ButtonsWrapper>
					<Button xs onClick={submitVehicleDetails} disabled={vehicleDetailsDisabled}>
						{strings('add')}
					</Button>
				</ButtonsWrapper>
				{vehicleDetails?.length > 0 && (
					<>
						<PageLevelTableWrapper>
							<Table
								data={vehicleDetails}
								columns={VehicleColumsn}
								rowsToDisplay={vehicleDetails.length}
								totalItemCount={vehicleDetails?.length}
								page={0}
								rowClickable={false}
								hideFooter
								// fullHeight
								noScroll
							/>
						</PageLevelTableWrapper>
					</>
				)}
			</>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<Select
					id={'select-order'}
					label={'Order | SPO | Supplier'}
					placeholder={'Select Order Id'}
					data={orderList}
					value={selectedOrder ?? {}}
					primaryKey='fulfillment_plan_id'
					displayKey='display_name'
					onChange={selectOrderHandler}
					disabeld={edit}
				/>
				{selectedOrder && (
					<>
						<>
							<Input id='buyer' label='Buyer' value={displayName(selectedOrder?.buyer)} disabled />
							<Select
								id={'select-supplier'}
								label={'Supplier'}
								placeholder={'Select supplier'}
								data={supplierList}
								value={selectedSupplier}
								primaryKey='user_id'
								displayKey='display_name'
								onChange={(val) => {
									setSelectedSupplier(val)
								}}
								disabeld={edit}
							/>
						</>

						<BiInputWrapper>
							<Input
								id='supply-person'
								label={strings('supply', 'person')}
								placeholder={strings('supply', 'person')}
								value={selectedOrder?.supply_manager?.full_name ?? '-'}
								disabled
							/>
							<Select
								id='logistics-handler'
								label={strings('logistics_handler')}
								placeholer={strings('select', 'logistics_handler')}
								value={logisticsHandlerTypes?.find((item) => item.key === formState?.logistics_handler.value) ?? {}}
								data={logisticsHandlerTypes}
								displayKey='label'
								primaryKey='key'
								onChange={(val) => updateState('logistics_handler')(val.key)}
								// disabled
							/>
						</BiInputWrapper>

						<BiInputWrapper>
							<Input
								id='region'
								label={strings('region')}
								placeholer={strings('enter', 'region')}
								value={formState?.region?.value}
								disabled
							/>
							<Input id='pending-quantity' label={strings('pending_quantity')} value={selectedSupplier?.pending_weight} disabled />
						</BiInputWrapper>
						<BiInputWrapper>
							<Input label={`Price per KG`} value={displayAmount(selectedSupplier?.price_per_unit)} disabled />
							<Input
								label={'Expected amount'}
								value={displayAmount(selectedSupplier?.price_per_unit * formState?.quantity?.value * 1000)}
								disabled
							/>
						</BiInputWrapper>
						<BiInputWrapper>
							<Select
								id={formState?.input_type?.id}
								label={formState?.input_type?.label}
								placeholder={formState?.input_type?.placeholder}
								data={inputTypes}
								value={inputTypes?.find((item) => item?.key === formState?.input_type?.value) ?? {}}
								primaryKey={formState?.input_type?.primary_key}
								displayKey={formState?.input_type?.display_key}
								disabled={formState?.input_type?.disabled}
								required={formState?.input_type?.required}
								onChange={(val) => updateState('input_type')(val?.key)}
							/>
							<Input
								id='quantity'
								type='number'
								min={0}
								label={strings('quantity_mts')}
								placeholder={strings('enter', 'quantity')}
								value={formState?.quantity?.value}
								onChange={updateState('quantity')}
							/>
						</BiInputWrapper>

						<BiInputWrapper>
							<Calendar
								id='plan-start-date'
								label={strings('plan_start_date')}
								minDate={new Date().getTime()}
								value={formState?.plan_start_date?.value}
								onChange={updateState('plan_start_date')}
							/>
							<Select
								id={formState?.payment_status?.id}
								label={formState?.payment_status?.label}
								placeholder={formState?.payment_status?.placeholder}
								data={dsispatchPaymentStatus}
								value={dsispatchPaymentStatus?.find((item) => item?.key === formState?.payment_status?.value) ?? {}}
								primaryKey={formState?.payment_status?.primary_key}
								displayKey={formState?.payment_status?.display_key}
								disabled={formState?.payment_status?.disabled}
								required={formState?.payment_status?.required}
								onChange={(val) => updateState('payment_status')(val?.key)}
							/>
						</BiInputWrapper>
						{renderVehicleDetails()}
						<RadioButtonsWrapper>
							<RadioButtonWrapper>
								<RadioButton
									id='services-radio-yes'
									size='1.125'
									checked={formState?.planned?.value === 'TRUE'}
									value={'TRUE'}
									onChange={() => {
										updateState('planned')('TRUE')
									}}
								/>
								<Label>{strings('planned')}</Label>
							</RadioButtonWrapper>
							<RadioButtonWrapper>
								<RadioButton
									id='services-radio-no'
									size='1.125'
									checked={formState?.planned?.value === 'FALSE'}
									value={'FALSE'}
									onChange={() => {
										updateState('planned')('FALSE')
									}}
								/>
								<Label>{strings('unplanned')}</Label>
							</RadioButtonWrapper>
						</RadioButtonsWrapper>
					</>
				)}
				{selectedOrder?.payment_terms_list && (
					<ListWrapper>
						<>{strings('payment_terms')}</>
						{selectedOrder?.payment_terms_list?.map((item) => (
							<ListItemWrapper>
								<div>{`${item?.percentage}% ${capitalizeWord(item?.payment_term ?? '')} ${
									item?.condition
										? item?.condition
												?.split('_')
												?.map((item) => capitalizeWord(item ?? ''))
												?.join(' ')
										: ''
								} for ${item?.tenure_in_days} days`}</div>
							</ListItemWrapper>
						))}
					</ListWrapper>
				)}

				{/* <Textarea label={strings('remark')} value={formState?.new_remark?.value} onChange={updateState('new_remark')} /> */}
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button small disabled={checkDisabled} onClick={onAddHandler}>
					{strings('add')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddDispatchPlan
